/*  eslint-disable react/prop-types */
import React, { useContext, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
    Typography,
    Divider,
    Select,
    Flex,
    Radio,
    RadioChangeEvent,
    DatePicker,
    Button,
    type RangePickerProps,
    Row,
    Col,
} from '@app/ui';
import {
    getTranslatedSocialMetricOptions,
    getTranslatedVisualizationOptions,
    getTranslatedVisualizationOptionsFollowers,
    getTranslatedTotalNames,
    platformOptions,
} from './socialbenchmarking-filters';
import { team } from '@app/constants/sbAccountGroupTypes';
import { SocialBenchmarkingContext } from './SocialBenchmarkingContext';
const { Text } = Typography;
const { RangePicker } = DatePicker;
import { VisualizationOption } from './SocialBenchmarkingFilters';

const FiltersForm = ({
    leagues,
    favoritesList,
    getSocialBenchmarkingAccountGroups,
    isLoading,
    onCollapseFilters = null,
}) => {
    const { t } = useTranslation(['common', 'benchmarking']);

    // Get translated options
    const socialMetricOptions = useMemo(
        () => getTranslatedSocialMetricOptions(t),
        [t],
    );
    const visualizationOptions = useMemo(
        () => getTranslatedVisualizationOptions(t),
        [t],
    );
    const visualizationOptionsFollowers = useMemo(
        () => getTranslatedVisualizationOptionsFollowers(t),
        [t],
    );
    const totalNames = useMemo(() => getTranslatedTotalNames(t), [t]);

    const {
        datePeriodicityValue,
        setDatePeriodicityValue,
        socialMetricFilterValue,
        setSocialMetricFilterValue,
        leagueFilterValue,
        setLeagueFilterValue,
        accountTypeOptionIds,
        accountTypeOptions,
        translatedAccountTypeOptions,
        accountTypeValues,
        setAccountTypeValues,
        setAccountTypeOptions,
        isFollowerMetric,
        setSelectedVisualizationOption,
        isMediaTypeVisual,
        selectedVisualizationOption,
        platformsValues,
        mediaTypeOptionIds,
        setPlaformsValues,
        mediaOptions,
        setMediaTypesValues,
        mediaTypesValues,
        setCustomDateRange,
        customDateRange,
        setCurrentVisualizationValue,
        currentSocialMetricFilterValue,
        setCurrentSocialMetricFilterValue,
        socialMetric,
        setSelectedHeader,
        setTableSorting,
        currentVisualizationValue,
        setSelectedAccountGroup,
        setAppliedFilters,
    } = useContext(SocialBenchmarkingContext);

    useEffect(() => {
        if (!isFollowerMetric) return;
        setMediaTypesValues([]);
    }, [isFollowerMetric]);

    const isLeagueFilterValueFavorite =
        typeof leagueFilterValue === 'string' &&
        leagueFilterValue.startsWith('favorite');

    useEffect(() => {
        if (leagueFilterValue) {
            let currentAccountTypeIds = accountTypeOptionIds;

            isLeagueFilterValueFavorite
                ? (currentAccountTypeIds = favoritesList.accountTypeIds)
                : (currentAccountTypeIds =
                      leagues.find(league => league.id === leagueFilterValue)
                          ?.accountTypeIds || []);

            const filteredAccountTypeOptions =
                translatedAccountTypeOptions.filter(item =>
                    currentAccountTypeIds.includes(item.value),
                );

            // Compare arrays by content, not reference, to prevent infinite loops
            const currentOptionsStr = JSON.stringify(
                accountTypeOptions.map(opt => ({
                    value: opt.value,
                    name: opt.name,
                })),
            );
            const filteredOptionsStr = JSON.stringify(
                filteredAccountTypeOptions.map(opt => ({
                    value: opt.value,
                    name: opt.name,
                })),
            );

            // Only update if the content actually changed
            if (currentOptionsStr !== filteredOptionsStr) {
                // Clear account type values if options changed
                setAccountTypeValues([]);
                setAccountTypeOptions(filteredAccountTypeOptions);
            }
        }
    }, [
        leagueFilterValue,
        leagues,
        favoritesList,
        translatedAccountTypeOptions,
        accountTypeOptionIds,
        // Removed accountTypeOptions from dependencies to prevent infinite loop
        // We compare by content inside the effect instead
    ]);

    const showFavorites =
        favoritesList &&
        favoritesList.favorites &&
        favoritesList.favorites.length;
    const leagueOptions = useMemo(() => {
        const preselectedLeagues = leagues
            .filter(({ preselected }) => preselected)
            .map(({ name, id: value }) => {
                return {
                    label: name,
                    value,
                };
            })
            .sort((a, b) => a.label.localeCompare(b.label));

        const nonpreselectedLeagues = leagues
            .filter(({ preselected }) => !preselected)
            .map(({ name, id: value }) => {
                return {
                    label: name,
                    value,
                };
            })
            .sort((a, b) => a.label.localeCompare(b.label));

        const favoritesOption = showFavorites
            ? {
                  label: t('socialBenchmarking.filters.favorites', {
                      ns: 'benchmarking',
                  }),
                  value: `favorites_${favoritesList.id}`,
              }
            : null;

        const groups = [];

        // Add Favorites group if available
        if (showFavorites && favoritesOption) {
            groups.push({
                label: (
                    <span>
                        {t('socialBenchmarking.filters.favorites', {
                            ns: 'benchmarking',
                        })}
                    </span>
                ),
                title: t('socialBenchmarking.filters.favorites', {
                    ns: 'benchmarking',
                }),
                options: [favoritesOption],
            });
        }

        // Add Preselected Leagues group if available
        if (preselectedLeagues.length > 0) {
            groups.push({
                label: (
                    <span>
                        {t('socialBenchmarking.filters.preselectedLeagues', {
                            ns: 'benchmarking',
                        })}
                    </span>
                ),
                title: t('socialBenchmarking.filters.preselectedLeagues', {
                    ns: 'benchmarking',
                }),
                options: preselectedLeagues,
            });
        }

        // Add Other Leagues group if available
        if (nonpreselectedLeagues.length > 0) {
            groups.push({
                label: (
                    <span>
                        {t('socialBenchmarking.filters.otherLeagues', {
                            ns: 'benchmarking',
                        })}
                    </span>
                ),
                title: t('socialBenchmarking.filters.otherLeagues', {
                    ns: 'benchmarking',
                }),
                options: nonpreselectedLeagues,
            });
        }

        return groups;
    }, [leagues, favoritesList]);

    const currentVisualizationOptions: VisualizationOption[] = useMemo(() => {
        const baseOptions = isFollowerMetric
            ? visualizationOptionsFollowers
            : visualizationOptions;

        // Create a new array with the last option's name updated
        return baseOptions.map((option, index) => {
            if (index === baseOptions.length - 1) {
                return {
                    ...option,
                    name: totalNames[socialMetricFilterValue] || option.name,
                };
            }
            return option;
        });
    }, [
        isFollowerMetric,
        socialMetricFilterValue,
        visualizationOptions,
        visualizationOptionsFollowers,
        totalNames,
    ]);

    // Sets selected visualization option back to default (platform)
    // in case an invalid conbination of filters is selected
    useEffect(() => {
        if (socialMetricFilterValue === 'follower' && isMediaTypeVisual) {
            setSelectedVisualizationOption('platform');
        }
    }, [isMediaTypeVisual, socialMetricFilterValue]);

    const onChangeDatePeriodicity = (e: RadioChangeEvent) => {
        if (e.target.value !== 'custom') {
            setCustomDateRange({
                startDate: null,
                endDate: null,
            });
        }
        setDatePeriodicityValue(e.target.value);
    };

    const disabledDate: RangePickerProps['disabledDate'] = current => {
        // Can not select days before today and today
        return current && current > moment().subtract(2, 'days').endOf('day');
    };

    const onChangeDate: RangePickerProps['onChange'] = (dates, dateStrings) => {
        if (dates) {
            setCustomDateRange({
                startDate: dateStrings[0],
                endDate: dateStrings[1],
            });
        } else {
            setCustomDateRange({
                startDate: null,
                endDate: null,
            });
        }
    };

    const shouldDisableSubmitButton = () => {
        if (datePeriodicityValue === 'custom') {
            return customDateRange.startDate && customDateRange.endDate;
        }
        return true;
    };

    const handleClearAllFilters = () => {
        setSocialMetricFilterValue(socialMetricOptions[0].value);
        setSelectedVisualizationOption(visualizationOptions[0].value);
        setCustomDateRange({
            startDate: null,
            endDate: null,
        });
        setPlaformsValues([]);
        setAccountTypeValues([]);
        setMediaTypesValues([]);
    };

    const onClickUpdateButton = () => {
        updateFilters(leagueFilterValue);
        //todo add tracking
        if (onCollapseFilters) {
            onCollapseFilters();
        }
    };

    const updateFilters = useCallback(
        async leagueValue => {
            // Selecting none should be the same as selecting everything
            const value = leagueFilterValue ? leagueFilterValue : leagueValue;
            const showLeagues = !Number.isNaN(parseInt(value));
            const leagueId = showLeagues ? parseInt(value) : null;
            const favoriteId =
                !showLeagues && showFavorites
                    ? value
                        ? parseInt(value.split('_')[1])
                        : favoritesList?.id
                    : null;

            const platformOptionIds = platformOptions.map(
                platformOption => platformOption.value,
            );

            setCurrentVisualizationValue(selectedVisualizationOption);
            // Find visualization option - check both regular and followers variants
            // Use the appropriate options based on current metric
            const optionsToSearch = isFollowerMetric
                ? visualizationOptionsFollowers
                : visualizationOptions;
            const foundOption = optionsToSearch.find(
                v => v.value === selectedVisualizationOption,
            );
            const visualizationQueryValues =
                foundOption?.queryValues || foundOption?.array;

            const filters = {
                metric: socialMetricFilterValue,
                periodicity: datePeriodicityValue,
                platformIds:
                    platformsValues.length > 0
                        ? platformsValues
                        : platformOptionIds,
                mediaTypes:
                    mediaTypesValues.length > 0
                        ? mediaTypesValues
                        : mediaTypeOptionIds,
                accountTypes:
                    accountTypeValues.length > 0
                        ? accountTypeValues
                        : accountTypeOptionIds,
                ...(leagueId ? { leagueIds: [leagueId] } : {}),
                ...(favoriteId ? { favoriteIds: [favoriteId] } : {}),
                types: [team],
                visualization: visualizationQueryValues,
                ...(datePeriodicityValue === 'custom'
                    ? {
                          startDate: customDateRange.startDate,
                          endDate: customDateRange.endDate,
                      }
                    : {}),
            };

            setSelectedAccountGroup(null);
            await getSocialBenchmarkingAccountGroups(filters);

            // Update applied filters after successful API call
            setAppliedFilters({
                socialMetric: socialMetricFilterValue,
                league: leagueId
                    ? leagueId.toString()
                    : value && value.startsWith('favorites_')
                      ? value
                      : null,
                visualization: selectedVisualizationOption,
                datePeriodicity: datePeriodicityValue,

                platforms: platformsValues.length > 0 ? platformsValues : [],
                accountTypes:
                    accountTypeValues.length > 0 ? accountTypeValues : [],

                mediaTypes: mediaTypesValues.length > 0 ? mediaTypesValues : [],

                customDateRange:
                    datePeriodicityValue === 'custom'
                        ? customDateRange
                        : { startDate: null, endDate: null },
            });

            if (socialMetricFilterValue !== currentSocialMetricFilterValue) {
                setCurrentSocialMetricFilterValue(socialMetricFilterValue);
                setSelectedHeader('itemCount');
                setTableSorting([
                    {
                        id: 'rank',
                        desc: false,
                    },
                ]);
            }

            return filters;
        },
        [
            socialMetricFilterValue,
            datePeriodicityValue,
            mediaTypesValues,
            platformsValues,
            accountTypeValues,
            leagueFilterValue,
            socialMetric,
            currentSocialMetricFilterValue,
            selectedVisualizationOption,
            currentVisualizationValue,
            customDateRange,
            showFavorites,
            isFollowerMetric,
            visualizationOptions,
            visualizationOptionsFollowers,
            favoritesList,
        ],
    );

    useEffect(() => {
        (async () => {
            if ((!leagues || !leagues.length) && !showFavorites) return;
            const defaultLeagueValue = showFavorites
                ? `favorites_${favoritesList.id}`
                : leagueOptions[0].value;
            setLeagueFilterValue(defaultLeagueValue);
            await updateFilters(defaultLeagueValue);
        })();
    }, [leagues, favoritesList]);

    return (
        <div>
            <Text>
                {t('socialBenchmarking.filters.inputs', { ns: 'benchmarking' })}
            </Text>
            <Divider
                style={{ margin: '0', color: '#F0F0F0', paddingBottom: '12px' }}
                type="horizontal"
            />
            <Flex gap={16} wrap="wrap" style={{ paddingBottom: '12px' }}>
                <Flex vertical flex={1} gap={4}>
                    <Text>{t('filters.socialMetric', { ns: 'common' })}</Text>
                    <Select
                        placeholder={t(
                            'socialBenchmarking.filters.selectSocialMetric',
                            { ns: 'benchmarking' },
                        )}
                        options={socialMetricOptions}
                        fieldNames={{ label: 'name', value: 'value' }}
                        value={socialMetricFilterValue}
                        onChange={setSocialMetricFilterValue}
                        disabled={isLoading}
                    />
                </Flex>

                <Flex vertical flex={1} gap={4}>
                    <Text>{t('filters.league', { ns: 'common' })}</Text>
                    <Select
                        placeholder={t(
                            'socialBenchmarking.filters.selectLeagues',
                            { ns: 'benchmarking' },
                        )}
                        options={leagueOptions}
                        value={leagueFilterValue}
                        onChange={setLeagueFilterValue}
                        disabled={isLoading}
                    />
                </Flex>
            </Flex>
            <Flex gap={16} wrap="wrap" style={{ paddingBottom: '12px' }}>
                <Flex vertical flex={1} gap={4}>
                    <Text>
                        {t('filters.visualizationType', { ns: 'common' })}
                    </Text>
                    <Select
                        placeholder={t(
                            'socialBenchmarking.filters.selectVisualizationType',
                            { ns: 'benchmarking' },
                        )}
                        fieldNames={{ label: 'name', value: 'value' }}
                        options={currentVisualizationOptions}
                        value={selectedVisualizationOption}
                        onChange={setSelectedVisualizationOption}
                        disabled={isLoading}
                    />
                </Flex>

                <Flex vertical flex={1} gap={4}>
                    <Text>{t('filters.dateRange', { ns: 'common' })}</Text>
                    <Flex align="center" gap={8} style={{ width: '100%' }}>
                        <Radio.Group
                            onChange={onChangeDatePeriodicity}
                            value={datePeriodicityValue}
                            style={{ flex: 1 }}
                            disabled={isLoading}
                        >
                            <Radio value={'weekly'}>
                                {t('socialBenchmarking.filters.weekly', {
                                    ns: 'benchmarking',
                                })}
                            </Radio>
                            <Radio value={'monthly'}>
                                {t('socialBenchmarking.filters.monthly', {
                                    ns: 'benchmarking',
                                })}
                            </Radio>
                            <Radio value={'custom'}>
                                {t('socialBenchmarking.filters.custom', {
                                    ns: 'benchmarking',
                                })}
                            </Radio>
                        </Radio.Group>
                        <RangePicker
                            onChange={onChangeDate}
                            disabledDate={disabledDate}
                            disabled={datePeriodicityValue !== 'custom'}
                            placeholder={[
                                t('filters.startDate', { ns: 'common' }),
                                t('filters.endDate', { ns: 'common' }),
                            ]}
                            style={{ flex: 1 }}
                        />
                    </Flex>
                </Flex>
            </Flex>
            <Text style={{ paddingTop: '12px' }}>
                {t('filters.title', { ns: 'common' })}
            </Text>
            <Divider
                style={{ margin: '0', color: '#F0F0F0', paddingBottom: '12px' }}
                type="horizontal"
            />
            <Flex gap={16} wrap="wrap" style={{ paddingBottom: '12px' }}>
                <Flex vertical flex={1} gap={4}>
                    <Text>{t('filters.platforms', { ns: 'common' })}</Text>
                    <Select
                        placeholder={t('filters.selectPlatforms', {
                            ns: 'common',
                        })}
                        mode="multiple"
                        options={platformOptions}
                        value={platformsValues}
                        onChange={setPlaformsValues}
                        maxTagCount={1}
                        maxTagPlaceholder={`...`}
                        disabled={isLoading}
                    />
                </Flex>

                <Flex vertical flex={1} gap={4}>
                    <Text>{t('filters.accountTypes', { ns: 'common' })}</Text>
                    <Select
                        placeholder={t('filters.selectAccountTypes', {
                            ns: 'common',
                        })}
                        mode="multiple"
                        options={accountTypeOptions}
                        value={accountTypeValues}
                        onChange={setAccountTypeValues}
                        maxTagCount={1}
                        maxTagPlaceholder={`...`}
                        disabled={isLoading}
                    />
                </Flex>

                <Flex vertical flex={1} gap={4}>
                    <Text>{t('filters.mediaTypes', { ns: 'common' })}</Text>
                    <Select
                        placeholder={t('filters.selectMediaTypes', {
                            ns: 'common',
                        })}
                        mode="multiple"
                        options={mediaOptions}
                        value={mediaTypesValues}
                        onChange={setMediaTypesValues}
                        maxTagCount={1}
                        maxTagPlaceholder={`...`}
                        disabled={isLoading || isFollowerMetric}
                    />
                </Flex>
            </Flex>
            <Row gutter={16}>
                <Col span={24}>
                    <Flex gap="small" wrap justify="flex-end" align="flex-end">
                        <Button
                            type="primary"
                            color="default"
                            variant="solid"
                            icon={<i className="fa-regular fa-x -m-r-2" />}
                            onClick={handleClearAllFilters}
                        >
                            {t('filters.clearAllFilters', { ns: 'common' })}
                        </Button>
                        <Button
                            type="primary"
                            color="primary"
                            variant="solid"
                            icon={
                                <i className="fa-regular fa-arrow-rotate-right -m-r-2" />
                            }
                            onClick={() => onClickUpdateButton()}
                            disabled={!shouldDisableSubmitButton()}
                            loading={isLoading}
                        >
                            {t('filters.updateFilters', { ns: 'common' })}
                        </Button>
                    </Flex>
                </Col>
            </Row>
        </div>
    );
};

export default FiltersForm;
